import { Edit, GraduationCap, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type Tuition,
  useGetStudentTuitionDetails,
} from '~entities/student/api/get-student-tuition-details';
import { useUpdateStudentTuitionFeeMutation } from '~entities/student/api/update-student-tuition-fee';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';

interface AlterMonthlyTuitionProps {
  studentId: string;
}

export const AlterMonthlyTuition = ({
  studentId,
}: AlterMonthlyTuitionProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const { data } = useGetStudentTuitionDetails({ studentId });
  const filteredData = useMemo(() => {
    const classIds: Record<string, boolean> = {};
    const uniqueClasses: Array<Tuition> = [];

    data?.forEach((classItem) => {
      const key = classItem.subscriptionId ?? classItem.enrollmentId;
      if (!classIds[key]) {
        classIds[key] = true;
        uniqueClasses.push(classItem);
      }
    });

    return uniqueClasses;
  }, [data]);

  const isEmpty = !data || filteredData.length === 0;

  const [selectedClass, setSelectedClass] = useState<string>(
    filteredData[0]?.subscriptionId ??
      filteredData[0]?.enrollmentId ??
      '',
  );

  const [newAmount, setNewAmount] = useState(
    (filteredData[0]?.finalAmount ?? filteredData[0]?.baseAmount)?.toFixed(2) ??
      '0.00',
  );

  const [open, setOpen] = useState(false);
  const selectedTuition = filteredData.find((c) => {
    const key = c.subscriptionId ?? c.enrollmentId;
    return key === selectedClass;
  });

  const currencySymbol = currencyDetails?.symbol ?? '$';

  const getPayableAmount = useCallback(
    (tuition: Tuition) => Number(tuition.finalAmount),
    [],
  );

  const getDiscountAmount = useCallback(
    (tuition: Tuition) =>
      Math.max(0, Number(tuition.baseAmount) - getPayableAmount(tuition)),
    [getPayableAmount],
  );

  const resetAmountsForClass = useCallback(
    (tuition: Tuition) => {
      const payableAmount = getPayableAmount(tuition);

      setNewAmount(payableAmount.toFixed(2));
    },
    [getPayableAmount],
  );

  useEffect(() => {
    if (!filteredData.length) return;

    const selectedExists = filteredData.some((c) => {
      const key = c.subscriptionId ?? c.enrollmentId;
      return key === selectedClass;
    });

    if (!selectedExists) {
      const firstClass = filteredData[0];
      if (firstClass) {
        setSelectedClass(firstClass.subscriptionId ?? firstClass.enrollmentId);
      }
    }
  }, [filteredData, selectedClass]);

  useEffect(() => {
    if (open && selectedTuition) {
      resetAmountsForClass(selectedTuition);
    }
  }, [open, resetAmountsForClass, selectedClass, selectedTuition]);

  const mutation = useUpdateStudentTuitionFeeMutation();

  const handleUpdateTuitionDetails = async () => {
    if (!selectedTuition) return;

    const newTuitionAmount = parseFloat(newAmount);
    if (Number.isNaN(newTuitionAmount) || newTuitionAmount < 1) return;

    try {
      await mutation.mutateAsync({
        studentId,
        subscriptionId: selectedTuition.subscriptionId,
        enrollmentId: selectedTuition.enrollmentId,
        amount: newTuitionAmount,
        baseAmount: newTuitionAmount,
        finalAmount: newTuitionAmount,
        discountAmount: 0,
      });
      toast.success('Tuition details updated successfully');
      setOpen(false); // Close dialog on success
    } catch (error) {
      console.error('Error updating tuition details:', error);
      toast.error('Failed to update tuition details');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='outline' className='w-full'>
          <Edit />
          Alter Monthly Tuition
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Alter Monthly Tuition</DialogTitle>
          <DialogDescription>
            Change the tuition amount for specific enrolled classes.
          </DialogDescription>
        </DialogHeader>
        {isEmpty ? (
          <div className='py-16 text-center'>
            <div className='mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-200 to-pink-200'>
              <GraduationCap className='size-8 text-purple-500' />
            </div>
            <h3 className='mb-2 text-lg font-medium text-gray-600'>
              No enrolled classes found
            </h3>
            <p className='text-sm text-gray-500'>
              This student is not currently enrolled in any classes with
              tuition.
            </p>
          </div>
        ) : (
          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <Label>Select Enrolled Class</Label>
              <div className='max-h-[200px] space-y-3 overflow-y-auto rounded-md border p-2'>
                <RadioGroup
                  value={selectedClass}
                  onValueChange={(value) => {
                    setSelectedClass(value);
                    const selected = filteredData.find((c) => {
                      const key = c.subscriptionId ?? c.enrollmentId;
                      return key === value;
                    });
                    if (selected) resetAmountsForClass(selected);
                  }}
                >
                  {filteredData.map((classItem) => (
                    <div
                      key={classItem.subscriptionId ?? classItem.enrollmentId}
                      className='flex items-start gap-2'
                    >
                      <RadioGroupItem
                        value={classItem.subscriptionId ?? classItem.enrollmentId}
                        id={`enrolled-class-${classItem.subscriptionId ?? classItem.enrollmentId}`}
                        className='mt-1'
                      />
                      <div className='flex flex-col gap-y-1'>
                        <Label
                          htmlFor={`enrolled-class-${classItem.subscriptionId ?? classItem.enrollmentId}`}
                          className='cursor-pointer font-medium'
                        >
                          {classItem.enrollmentName}
                        </Label>
                        <div className='text-sm text-muted-foreground'>
                          {currencySymbol}
                          {getPayableAmount(classItem).toFixed(2)} /{' '}
                          {classItem.billingCycle}
                          {getDiscountAmount(classItem) > 0 && (
                            <span className='ml-2'>
                              ({currencySymbol}
                              {classItem.baseAmount.toFixed(2)} before discount)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <p className='text-xs text-muted-foreground'>
                Tuition override applies only to the selected class
              </p>
            </div>

            <div className='grid gap-2'>
              <Label htmlFor='current-tuition'>
                Current Tuition Amount (full monthly)
              </Label>
              <Input
                id='current-tuition'
                value={selectedTuition?.baseAmount.toFixed(2) ?? '0.00'}
                disabled
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='new-tuition'>New Tuition Amount</Label>
              <Input
                type='number'
                id='new-tuition'
                placeholder='Enter new amount'
                value={newAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow digits and one decimal point
                  if (!/^\d*\.?\d*$/.test(value)) return;
                  setNewAmount(value);
                }}
                min={1}
                onKeyDown={(e) => {
                  if (
                    e.key === '.' ||
                    e.key === 'e' ||
                    e.key === 'E' ||
                    e.key === '-'
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              {Number(newAmount) < 1 && (
                <p className='text-xs text-red-500'>
                  Amount should not be less than 1
                </p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          {!isEmpty && (
            <Button
              disabled={
                !selectedTuition ||
                mutation.isPending ||
                getPayableAmount(selectedTuition) === Number(newAmount) ||
                isNaN(Number(newAmount)) ||
                Number(newAmount) < 1
              }
              onClick={handleUpdateTuitionDetails}
            >
              {mutation.isPending ? (
                <div className='flex items-center gap-2'>
                  <Loader2 className='mr-2 animate-spin' />
                  Saving
                </div>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
